// 단축키 안내 오버레이(F1 또는 ?). 키 표(hotkeys.keyHelpFor)를 키캡 모양으로 보여 준다. Esc·F1·?·바깥 누르기·닫기 단추로 닫는다.
// 화면 가운데에 놓이지만 열려 있는 동안 게임은 일시정지(ui/index.ts가 setPaused를 보낸다)라 플레이를 가리는 문제가 없다.
import { C, FONT, el } from './dom';
import { keyHelpFor, isHelpKey, type KeyLike } from './hotkeys';
import { viewH, viewW } from '../shared/viewport';
import { keymap } from '../shared/keymap';

/** 도움말이 열려 있을 때 키 하나를 어떻게 다룰지(순수): pass = 그대로 흘려보냄(Esc·F1·? 닫기), tab = 창 안 포커스 순환, block = 게임·다른 UI로 가지 못하게 막음 */
export function helpKeyRoute(e: KeyLike): 'pass' | 'tab' | 'block' {
  if (e.code === 'Escape' || isHelpKey(e)) return 'pass';
  if (e.code === 'Tab' && !e.ctrlKey && !e.metaKey && !e.altKey) return 'tab';
  // 브라우저 단축키(Ctrl·Cmd·Alt 조합)는 막지 않는다
  if (e.ctrlKey || e.metaKey || e.altKey) return 'pass';
  return 'block';
}

/** Tab 순환: 포커스 칸이 count개일 때 현재 위치(cur, 없으면 -1)에서 다음/이전 위치. 칸이 없으면 -1 */
export function trapFocusIndex(count: number, cur: number, backwards: boolean): number {
  if (count <= 0) return -1;
  if (cur < 0) return backwards ? count - 1 : 0;
  return (cur + (backwards ? count - 1 : 1)) % count;
}

interface InertLike { inert?: boolean }
/** parent의 자식 중 keep만 빼고 모두 inert로 만든다(포커스·클릭 차단). 되돌리면 원래 inert였던 것은 그대로 둔다 */
export function inertSiblings<T extends object>(parent: { children: ArrayLike<T> }, keep: T): () => void {
  const changed: InertLike[] = [];
  for (const node of Array.from(parent.children)) {
    const c = node as InertLike;
    if (node === keep || c.inert === true) continue;
    c.inert = true;
    changed.push(c);
  }
  return () => { for (const c of changed) c.inert = false; };
}

export class HelpOverlay {
  private readonly backdrop: HTMLDivElement;
  private readonly card: HTMLDivElement;
  private opened = false;
  private readonly closeBtn: HTMLButtonElement;
  private readonly grid: HTMLDivElement;
  private restoreInert: (() => void) | null = null;
  private returnFocus: HTMLElement | null = null;

  constructor(parent: HTMLElement, private readonly onClosed: () => void = () => {}) {
    this.backdrop = el('div', 'position:absolute;inset:0;z-index:70;display:none;align-items:center;justify-content:center;pointer-events:auto;background:rgba(6,8,18,.8);', parent);
    this.backdrop.dataset.testid = 'help-overlay';
    this.backdrop.addEventListener('pointerdown', (e) => { if (e.target === this.backdrop) this.close(); });
    this.backdrop.addEventListener('wheel', (e) => e.stopPropagation(), { passive: true });
    this.card = el('div', 'position:relative;box-sizing:border-box;max-height:100%;overflow:auto;padding:18px 22px 20px;border:2px solid #d9a441;border-radius:14px;color:#e6ecff;font-family:' + FONT + ';' +
      'background:linear-gradient(180deg,rgba(24,32,62,.98),rgba(11,16,34,.98));box-shadow:inset 0 0 0 1px rgba(0,0,0,.65),inset 0 0 0 4px rgba(242,193,78,.14),0 10px 34px rgba(0,0,0,.65);', this.backdrop);
    this.card.tabIndex = -1;
    this.card.setAttribute('role', 'dialog');
    this.card.setAttribute('aria-modal', 'true');
    this.card.setAttribute('aria-label', '단축키 안내');
    const head = el('div', 'display:flex;align-items:center;gap:12px;margin-bottom:12px;', this.card);
    el('div', 'flex:1 1 auto;font:900 22px ' + FONT + ';letter-spacing:1px;color:' + C.gold + ';text-shadow:0 1px 0 #000,0 2px 4px rgba(0,0,0,.7);', head, '단축키 안내');
    const close = el('button', 'flex:0 0 auto;cursor:pointer;min-height:36px;padding:0 14px;border-radius:999px;border:1.5px solid #e2b24a;background:linear-gradient(180deg,#2c3a68,#121a36);color:#ffe7a8;font:800 14px ' + FONT + ';', head, '닫기 (Esc)') as HTMLButtonElement;
    this.closeBtn = close;
    close.type = 'button';
    close.dataset.testid = 'help-close';
    close.addEventListener('click', () => this.close());
    this.grid = el('div', 'display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:16px 26px;', this.card);
    el('div', 'margin-top:14px;font:500 12.5px/1.4 ' + FONT + ';color:#9fb0d8;', this.card, '버튼이나 아이템에 마우스를 올리면 설명이 나와요. 설정에서 화면 크기(UI 크기)를 바꿀 수 있어요.');
    // 열려 있는 동안 키는 이 창이 먼저 받는다(캡처): Esc는 이 창만 닫고, Tab은 창 안에서만 돌고,
    // 그 밖의 키는 게임 입력·다른 창으로 새지 않게 막는다(타이틀에서 F1→Enter로 뒤쪽 시작 단추가 눌리던 것). F1·?는 토글을 위해 흘려보낸다
    window.addEventListener('keydown', (e) => {
      if (!this.opened) return;
      const r = helpKeyRoute(e);
      if (e.code === 'Escape') {
        e.stopImmediatePropagation();
        e.preventDefault();
        this.close();
      } else if (r === 'tab') {
        e.stopImmediatePropagation();
        e.preventDefault();
        this.cycleFocus(e.shiftKey);
      } else if (r === 'block') {
        // 기본 동작(포커스된 닫기 단추의 Enter·Space 클릭)은 살린다
        e.stopImmediatePropagation();
      }
    }, true);
  }

  private focusables(): HTMLElement[] {
    return Array.from(this.card.querySelectorAll<HTMLElement>('button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'));
  }

  private cycleFocus(backwards: boolean): void {
    const list = this.focusables();
    const i = trapFocusIndex(list.length, list.indexOf(document.activeElement as HTMLElement), backwards);
    if (i >= 0) list[i].focus({ preventScroll: true });
    else this.card.focus({ preventScroll: true });
  }

  /** 지금 키 배치로 표를 다시 그린다(열 때만) */
  private build(): void {
    this.grid.textContent = '';
    for (const g of keyHelpFor(keymap.preset)) {
      const col = el('div', 'min-width:0;', this.grid);
      el('div', 'margin-bottom:8px;padding-bottom:5px;border-bottom:1px solid rgba(242,193,78,.4);font:900 15px ' + FONT + ';color:#fff;', col, g.title);
      for (const r of g.rows) {
        const row = el('div', 'display:flex;align-items:center;gap:10px;margin:6px 0;', col);
        const keys = el('div', 'flex:0 0 auto;display:flex;flex-wrap:wrap;gap:4px;min-width:84px;max-width:132px;', row);
        for (const k of r.keys) {
          el('span', 'min-width:22px;padding:2px 7px;box-sizing:border-box;border-radius:6px;border:1.5px solid ' + C.gold + ';background:linear-gradient(180deg,#26335a,#10162a);box-shadow:0 2px 0 rgba(0,0,0,.5);' +
            'color:' + C.gold + ';font:800 13px ' + FONT + ';text-align:center;white-space:nowrap;', keys, k);
        }
        el('div', 'flex:1 1 auto;min-width:0;font:600 14px/1.35 ' + FONT + ';color:#dfe6ff;', row, r.label);
      }
    }
  }

  get isOpen(): boolean { return this.opened; }

  open(): void {
    if (this.opened) return;
    this.opened = true;
    this.build();
    this.returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.backdrop.style.display = 'flex';
    // 뒤쪽 UI는 포커스·클릭을 받지 못하게 하고, 도움말 닫기 단추로 포커스를 옮긴다
    if (this.backdrop.parentElement) this.restoreInert = inertSiblings(this.backdrop.parentElement, this.backdrop);
    this.closeBtn.focus({ preventScroll: true });
    // PC 넓은 화면은 키 표도 같이 키운다(1280x720 기준, 최대 1.5배). 폰·좁은 화면은 그대로
    const z = Math.max(1, Math.min(1.5, Math.min(viewW() / 1280, viewH() / 720)));
    this.card.style.zoom = String(z);
    this.card.style.width = Math.min(820, Math.floor((viewW() - 24) / z)) + 'px';
    this.card.style.maxHeight = Math.floor((viewH() - 24) / z) + 'px';
  }

  close(): void {
    if (!this.opened) return;
    this.opened = false;
    this.backdrop.style.display = 'none';
    this.restoreInert?.();
    this.restoreInert = null;
    // 열기 전에 있던 자리로 포커스를 돌려 준다(사라졌으면 포커스 없음)
    const back = this.returnFocus;
    this.returnFocus = null;
    if (back && back.isConnected) back.focus({ preventScroll: true });
    this.onClosed();
  }

  toggle(): void { if (this.opened) this.close(); else this.open(); }
}
